import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_BASE } from '../apiConfig';

const SiteSettingsContext = createContext({});

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loaded, setLoaded] = useState(false);

  const loadSettings = useCallback(async ({ initial = false } = {}) => {
    try {
      const res = await fetch(`${API_BASE}/site-admin/public-settings`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!res.ok) {
        if (initial) setLoaded(true);
        return;
      }

      const data = await res.json();
      setSettings(data || {});

      // Apply tab title (fallback to site name when title not explicitly set).
      if (data?.site_title) {
        document.title = data.site_title;
      } else if (data?.site_name) {
        document.title = data.site_name;
      }

      // Apply favicon.
      if (data?.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = data.favicon_url;
      }
    } catch (_e) {
      // Settings fetch failed — keep existing values silently.
    } finally {
      if (initial) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    let active = true;

    const refresh = () => {
      if (!active) return;
      loadSettings();
    };

    const onStorage = (event) => {
      if (event.key === 'bvb_site_settings_updated_at') refresh();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    const onFocus = () => refresh();
    const onCustomUpdate = () => refresh();

    loadSettings({ initial: true });

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    window.addEventListener('bvb:site-settings-updated', onCustomUpdate);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      active = false;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('bvb:site-settings-updated', onCustomUpdate);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadSettings]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadSettings();
      }
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [loadSettings]);

  return (
    <SiteSettingsContext.Provider value={{ settings, loaded }}>
      {/* Maintenance mode gate */}
      {loaded && settings.maintenance_mode === 'true' ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-10 max-w-lg text-center">
            <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">We'll Be Right Back</h1>
            <p className="text-gray-500">
              {settings.maintenance_message || 'BookViewBuy is currently undergoing scheduled maintenance. We should be back shortly. Thank you for your patience.'}
            </p>
          </div>
        </div>
      ) : children}
    </SiteSettingsContext.Provider>
  );
};
